import React from 'react';
import { DataRowHeader, Field, Section, Segmented, Toggle } from './ui';
import { DEFAULT_MEMBERSHIP_TYPE_OPTIONS, NON_MEMBER_GUEST_MEMBERSHIP_TYPE } from '../constants/membership';
import { normalizeAllowedMembershipTypes } from '../utils/membership';

/*
 * Every setting a competition has, in one place.
 *
 * These fields existed twice: once in NewCompView's "Customise Settings"
 * panel and once in the settings modal in App.js. Same fields, same state
 * shape, two hand-maintained copies with quietly different labels ("Draw
 * Method" against "Draw", "Bowls per Player" against "Bowls") and different
 * conditions on which of them show. Any change to a competition setting had
 * to be made twice or the two drifted.
 *
 * The differences that were real are now props rather than duplicated markup:
 *   variant     'create' shows team assignment whenever the format needs
 *               teams; 'edit' only shows it if the draw is already automatic,
 *               because changing it after the fact means re-generating teams.
 *   rulesAction the Competition Rules button — same control, different handler
 *   entries     the registration block, which only the edit view has
 *
 * On fields that come and go: the four match-type numbers (ends, target
 * score, time limit, sets) are DIMMED when they do not apply rather than
 * removed, so the form does not reflow under the pointer as you change match
 * type. Fields belonging to a section that is switched off entirely — the
 * finals lengths, the twilight point values — are still hidden, because
 * dimming a whole block of them is just noise.
 */

const FORMAT_OPTIONS = [
  { value: 'singles', label: 'Singles' },
  { value: 'pairs', label: 'Pairs' },
  { value: 'triples', label: 'Triples' },
  { value: 'fours', label: 'Fours' },
  { value: '2-4-2-pairs', label: '2-4-2 Pairs' },
  { value: 'custom', label: 'Custom' },
];

const MATCH_TYPE_OPTIONS = [
  { value: 'ends', label: 'Set ends', title: 'Plays a fixed number of ends' },
  { value: 'first-to', label: 'First to', title: 'Plays to a target score' },
  { value: 'timed', label: 'Timed', title: 'Plays to a time limit' },
  { value: 'sets', label: 'Sets', title: 'Best of a number of sets' },
];

const TOURNAMENT_OPTIONS = [
  { value: 'round-robin', label: 'Round robin', title: 'Every team plays every other' },
  { value: 'knockout', label: 'Knockout', title: 'Single elimination' },
  { value: 'double-elimination', label: 'Double elim.', title: 'One loss before being out' },
];

const DRAW_OPTIONS = [
  { value: 'random', label: 'Random', title: 'Shuffles teams' },
  { value: 'rating-balanced', label: 'Rating balanced', title: 'Pairs teams by player rating' },
];

const TEAM_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'auto-random', label: 'Auto (random)' },
  { value: 'auto-rated', label: 'Auto (rated)' },
];

const FINALS_OPTIONS = [
  { value: 'final-only', label: 'Final only (top 2)' },
  { value: 'semi-final', label: 'Semis + final (top 4)' },
  { value: 'quarter-final', label: 'Quarters + semis + final (top 8)' },
];

/* Selecting a format implies a team size and a bowls count. Kept here so the
   create form and the settings modal cannot disagree about what a "triples"
   competition is. */
const FORMAT_DEFAULTS = {
  singles: { playersPerTeam: 1, bowlsPerPlayer: 4 },
  pairs: { playersPerTeam: 2, bowlsPerPlayer: 4 },
  triples: { playersPerTeam: 3, bowlsPerPlayer: 3 },
  fours: { playersPerTeam: 4, bowlsPerPlayer: 2 },
  '2-4-2-pairs': { playersPerTeam: 2, bowlsPerPlayer: 4 },
  custom: { playersPerTeam: 1, bowlsPerPlayer: 4 },
};

/** A whole-number field. The empty string is preserved so a box can be
 *  cleared while typing rather than snapping back to 0. */
function NumField({ dimmed, disabled, help, id, label, max, min, onChange, placeholder, step, value }) {
  return (
    <Field label={label} htmlFor={id} help={help} dimmed={dimmed}>
      <input
        id={id}
        className="w3-input"
        type="number"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        // A dimmed field is empty because it does not apply, not because it
        // is waiting to be filled in. Without the dash it reads as an
        // oversight — say so in the box rather than leaving it blank.
        placeholder={disabled ? '—' : placeholder}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)} />
    </Field>
  );
}

/** Pick several out of a numbered set — greens, rinks. `null` means all of
 *  them, which is how the form stores "no restriction". */
function ChipGroup({ count, label, name, onChange, prefix = '', selected }) {
  const all = Array.from({ length: count }, (_, i) => i + 1);
  const current = selected || all;
  return (
    <Field label={label}>
      <div className="gs-chip-group">
        {all.map(n => {
          const on = current.includes(n);
          return (
            <label key={n} className={`gs-chip${on ? ' gs-chip-on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                aria-label={`${name} ${n}`}
                onChange={() => {
                  const next = on ? current.filter(x => x !== n) : [...current, n].sort((a, b) => a - b);
                  // Never let the last one be turned off — a competition with
                  // no rinks cannot be drawn.
                  if (next.length === 0) return;
                  onChange(next.length === count ? null : next);
                }} />
              {prefix}{n}
            </label>
          );
        })}
        <span className="gs-chip-count">{current.length} of {count} selected</span>
      </div>
    </Field>
  );
}

/* Which membership types may enter. Nothing selected means no restriction:
   a competition is open to everyone unless an admin says otherwise, which is
   how every competition behaved before this setting existed. The club's own
   list of types is the source, plus the guest type — team registrations can
   create guest players, so a club has to be able to say whether that is
   allowed here. */
function MembershipTypeChips({ clubOptions, onChange, selected }) {
  const current = normalizeAllowedMembershipTypes(selected);
  const options = [];
  [...clubOptions, NON_MEMBER_GUEST_MEMBERSHIP_TYPE, ...current].forEach((opt) => {
    const label = String(opt || '').trim();
    // A type an admin picked and the club has since renamed away still shows,
    // or turning it off would be the only way to see it at all.
    if (label && !options.some(o => o.toLowerCase() === label.toLowerCase())) options.push(label);
  });
  const isOn = opt => current.some(c => c.toLowerCase() === opt.toLowerCase());
  return (
    <Field
      label="Who can enter"
      help={current.length
        ? 'Anyone whose membership type is not selected cannot register.'
        : 'No types selected — open to everyone.'}>
      <div className="gs-chip-group">
        {options.map(opt => (
          <label key={opt} className={`gs-chip${isOn(opt) ? ' gs-chip-on' : ''}`}>
            <input
              type="checkbox"
              checked={isOn(opt)}
              aria-label={`Allow ${opt}`}
              onChange={() => {
                const next = isOn(opt)
                  ? current.filter(c => c.toLowerCase() !== opt.toLowerCase())
                  : [...current, opt];
                // Back to nothing selected is "open to everyone", stored as
                // null so it reads the same as a competition never restricted.
                onChange(next.length ? next : null);
              }} />
            {opt}
          </label>
        ))}
        <span className="gs-chip-count">
          {current.length ? `${current.length} of ${options.length} allowed` : 'Open to everyone'}
        </span>
      </div>
    </Field>
  );
}

function CompetitionSettingsFields({ clubId, clubs, entries, form, rulesAction, setForm, variant = 'create' }) {
  // Field ids have to be unique per instance or a label would point at the
  // other copy's input if both were ever mounted at once.
  const uid = React.useId();
  const fid = key => `${uid}${key}`;
  const set = patch => setForm(f => ({ ...f, ...patch }));
  const setNum = (key, parse) => v => set({ [key]: v === '' ? '' : parse(v) });
  const int = v => parseInt(v, 10);
  const num = v => parseFloat(v);

  const club = clubs.find(c => String(c.id) === String(clubId));
  const clubGreens = club?.numberOfGreens || 1;
  const clubRinks = club?.numberOfRinks || 6;
  const membershipOptions = (club && Array.isArray(club.membershipTypeOptions) && club.membershipTypeOptions.length)
    ? club.membershipTypeOptions
    : DEFAULT_MEMBERSHIP_TYPE_OPTIONS;

  const isRoundRobin = form.tournamentType === 'round-robin';
  const isTwilight = form.scoringMethod === 'twilight';
  const needsTeams = form.format !== 'singles';
  const isAutoTeams = form.teamAssignment === 'auto-random' || form.teamAssignment === 'auto-rated';
  const showTeamAssignment = needsTeams && (variant === 'create' || isAutoTeams);
  const rounds = parseInt(form.numberOfRounds, 10) || 0;

  // A fragment, not a wrapper element: the section separators are drawn by
  // `.gs-section + .gs-section`, which only matches actual siblings. Wrapped
  // in a div, the caller's own leading section (the modal's "The basics")
  // would butt straight up against the first one here with no rule and no
  // space between them.
  return (
    <>
      <Section label="How it's played">
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
          <Field label="Format">
            <Segmented
              ariaLabel="Format"
              options={FORMAT_OPTIONS}
              value={form.format}
              onChange={v => set({ format: v, ...(FORMAT_DEFAULTS[v] || {}) })} />
          </Field>
          <Field label="Match type">
            <Segmented
              ariaLabel="Match type"
              options={MATCH_TYPE_OPTIONS}
              value={form.matchType}
              // First-to has no drawn matches, so a draw-points value would
              // be dead data. Zero it as the type is chosen.
              onChange={v => set({ matchType: v, ...(v === 'first-to' ? { drawPoints: 0 } : {}) })} />
          </Field>
        </div>

        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)', marginTop: 16 }}>
          <Field label="Bowls per player">
            <Segmented
              ariaLabel="Bowls per player"
              options={[2, 3, 4]}
              value={form.bowlsPerPlayer}
              onChange={v => set({ bowlsPerPlayer: parseInt(v, 10) })} />
          </Field>
          <NumField
            id={fid('gs-ends')} label="Ends"
            dimmed={form.matchType !== 'ends'} disabled={form.matchType !== 'ends'}
            value={form.matchType === 'ends' ? form.numberOfEnds : ''}
            onChange={setNum('numberOfEnds', int)} />
          <NumField
            id={fid('gs-target')} label="Target score"
            dimmed={form.matchType !== 'first-to'} disabled={form.matchType !== 'first-to'}
            value={form.matchType === 'first-to' ? form.targetScore : ''}
            onChange={setNum('targetScore', int)} />
          <NumField
            id={fid('gs-time')} label="Time limit (min)"
            dimmed={form.matchType !== 'timed'} disabled={form.matchType !== 'timed'}
            value={form.matchType === 'timed' ? form.timeLimit : ''}
            onChange={setNum('timeLimit', int)} />
        </div>

        {form.matchType === 'sets' && (
          <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)', marginTop: 16 }}>
            <NumField id={fid('gs-ends-set')} label="Ends per set" value={form.endsPerSet} onChange={setNum('endsPerSet', int)} />
            <NumField id={fid('gs-sets')} label="Sets" value={form.numberOfSets} onChange={setNum('numberOfSets', int)} />
          </div>
        )}
      </Section>

      <Section label="The draw">
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
          <Field label="Tournament">
            <Segmented ariaLabel="Tournament" options={TOURNAMENT_OPTIONS} value={form.tournamentType}
              onChange={v => set({ tournamentType: v })} />
          </Field>
          <Field label="Draw method">
            <Segmented ariaLabel="Draw method" options={DRAW_OPTIONS} value={form.drawMethod || 'random'}
              onChange={v => set({ drawMethod: v })} />
          </Field>
        </div>

        {showTeamAssignment && (
          <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr', marginTop: 16 }}>
            <Field
              label="Team assignment"
              help={variant === 'edit' ? 'Changing this requires re-generating teams.' : undefined}>
              <Segmented ariaLabel="Team assignment" options={TEAM_OPTIONS} value={form.teamAssignment}
                onChange={v => set({ teamAssignment: v })} />
            </Field>
          </div>
        )}

        <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)', marginTop: 16 }}>
          <NumField
            id={fid('gs-rounds')} label="Rounds" min="1"
            help={isRoundRobin ? 'Blank calculates from the number of teams.' : undefined}
            dimmed={!isRoundRobin} disabled={!isRoundRobin}
            value={isRoundRobin ? form.numberOfRounds : ''}
            onChange={setNum('numberOfRounds', int)} />
          <NumField
            id={fid('gs-sections')} label="Sections" min="1"
            help="Pools to divide teams into. 1 for a single pool."
            value={form.numberOfSections}
            onChange={setNum('numberOfSections', int)} />
        </div>

        {isRoundRobin && (
          <div style={{ marginTop: 16 }}>
            <Toggle
              label="Finals series"
              checked={!!form.hasFinalsSeries}
              onChange={v => set({ hasFinalsSeries: v })} />
            {form.hasFinalsSeries && (
              <div style={{ marginTop: 12 }}>
                <Field label="Finals format">
                  <Segmented ariaLabel="Finals format" options={FINALS_OPTIONS}
                    value={form.finalsFormat || 'semi-final'}
                    onChange={v => set({ finalsFormat: v })} />
                </Field>
                {form.matchType === 'ends' && (
                  <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)', marginTop: 16 }}>
                    {form.finalsFormat === 'quarter-final' && (
                      <NumField id={fid('gs-qf')} label="Quarter-final ends" min="1" max="99"
                        placeholder={String(form.numberOfEnds || 18)}
                        value={form.quarterFinalEnds} onChange={setNum('quarterFinalEnds', int)} />
                    )}
                    {(form.finalsFormat === 'quarter-final' || form.finalsFormat === 'semi-final') && (
                      <NumField id={fid('gs-sf')} label="Semi-final ends" min="1" max="99"
                        placeholder={String(form.numberOfEnds || 18)}
                        value={form.semiFinalEnds} onChange={setNum('semiFinalEnds', int)} />
                    )}
                    <NumField id={fid('gs-f')} label="Final ends" min="1" max="99"
                      placeholder={String(form.numberOfEnds || 18)}
                      value={form.finalEnds} onChange={setNum('finalEnds', int)} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {clubGreens > 1 && (
            <ChipGroup label="Greens in use" name="Green" prefix="Green " count={clubGreens}
              selected={form.selectedGreens} onChange={v => set({ selectedGreens: v })} />
          )}
          <ChipGroup label="Rinks in use" name="Rink" count={clubRinks}
            selected={form.availableRinks} onChange={v => set({ availableRinks: v })} />
        </div>

        {isRoundRobin && rounds > 0 && (
          <div style={{ marginTop: 18 }}>
            <Field label={form.hasFinalsSeries ? 'Round and finals dates' : 'Round start times'}>
              <RoundTimes form={form} rounds={rounds} setForm={setForm} />
            </Field>
          </div>
        )}
      </Section>

      <Section label="Scoring">
        <div className="gs-field-grid" style={{ '--gs-cols': '1.6fr repeat(4, 1fr)' }}>
          <Field label="Method">
            <Segmented
              ariaLabel="Scoring method"
              options={[{ value: 'standard', label: 'Standard' }, { value: 'twilight', label: 'Twilight / social' }]}
              value={form.scoringMethod || 'standard'}
              onChange={v => set({ scoringMethod: v })} />
          </Field>
          <NumField id={fid('gs-win')} label="Win" value={form.winPoints} onChange={setNum('winPoints', num)} />
          <NumField
            id={fid('gs-draw')} label="Draw"
            dimmed={form.matchType === 'first-to'} disabled={form.matchType === 'first-to'}
            value={form.matchType === 'first-to' ? '' : form.drawPoints}
            onChange={setNum('drawPoints', num)} />
          <NumField id={fid('gs-loss')} label="Loss" value={form.lossPoints} onChange={setNum('lossPoints', num)} />
          <NumField id={fid('gs-bye')} label="Bye" help="When a team has no opponent."
            value={form.byePoints} onChange={setNum('byePoints', num)} />
        </div>

        {isTwilight && (
          <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(4, 1fr)', marginTop: 16 }}>
            <NumField id={fid('gs-set-pts')} label="Points per set won" value={form.pointsPerSetWon} onChange={setNum('pointsPerSetWon', num)} />
            <NumField id={fid('gs-end-pts')} label="Points per end won" value={form.pointsPerEndWon} onChange={setNum('pointsPerEndWon', num)} />
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
          {form.matchType !== 'first-to' && (
            <Toggle
              label={isTwilight ? 'Extra end when sets are tied' : 'Extra end on a draw'}
              checked={!!form.extraEndOnDraw}
              onChange={v => set({ extraEndOnDraw: v })} />
          )}
          <Toggle
            label="Count towards the club ladder"
            checked={form.includeInClubLeaderboard !== false}
            onChange={v => set({ includeInClubLeaderboard: v })} />
        </div>
      </Section>

      <Section label="Entries and rules">
        <MembershipTypeChips
          clubOptions={membershipOptions}
          selected={form.allowedMembershipTypes}
          onChange={v => set({ allowedMembershipTypes: v })} />
        {rulesAction}
        {entries}
      </Section>
    </>
  );
}

/* Which finals a competition actually plays, in the order they are played.
   Exported because the calendar needs the same answer. */
export const FINALS_STAGES = [
  { key: 'quarter-final', label: 'Quarter-final', formats: ['quarter-final'] },
  { key: 'semi-final', label: 'Semi-final', formats: ['quarter-final', 'semi-final'] },
  { key: 'final', label: 'Final', formats: ['quarter-final', 'semi-final', 'final-only'] },
];

export const finalsStagesFor = format =>
  FINALS_STAGES.filter(s => s.formats.includes(format || 'semi-final'));

/* A date and a time for each round, and for each final if there is a finals
   series. The default spaces rounds by the time limit plus a quarter of an
   hour, starting at 9am — the same arithmetic the two copies of this form
   each used to do for themselves.

   The finals sit in the same table rather than a panel of their own. A
   competition's schedule is one list of days people turn up on, and the
   final is the last of them, not a separate kind of thing. */
function RoundTimes({ form, rounds, setForm }) {
  const COLUMNS = [
    { key: 'round', label: 'Round', width: 110 },
    { key: 'date', label: 'Date', grow: true, primary: true },
    { key: 'time', label: 'Start', width: 140 },
  ];
  const gap = (parseInt(form.timeLimit, 10) || 60) + 15;
  const stages = form.hasFinalsSeries ? finalsStagesFor(form.finalsFormat) : [];

  const row = (label, key, date, time, onDate, onTime) => (
    <div className="gs-datarow" key={key}>
      <div className="gs-datarow-cell" style={{ width: 110, flexShrink: 0 }}>{label}</div>
      <div className="gs-datarow-cell gs-datarow-grow">
        <input className="w3-input" type="date" aria-label={`${label} date`}
          value={date} onChange={e => onDate(e.target.value)} />
      </div>
      <div className="gs-datarow-cell" style={{ width: 140, flexShrink: 0 }}>
        <input className="w3-input" type="time" aria-label={`${label} start time`}
          value={time} onChange={e => onTime(e.target.value)} />
      </div>
    </div>
  );

  return (
    <div>
      <DataRowHeader columns={COLUMNS} />
      {Array.from({ length: rounds }, (_, i) => {
        const n = i + 1;
        const total = 9 * 60 + i * gap;
        const defaultTime = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
        return row(
          `Round ${n}`, `r${n}`,
          (form.roundStartDates || {})[String(n)] || form.startDate || '',
          (form.roundStartTimes || {})[String(n)] || defaultTime,
          v => setForm(f => ({ ...f, roundStartDates: { ...(f.roundStartDates || {}), [String(n)]: v } })),
          v => setForm(f => ({ ...f, roundStartTimes: { ...(f.roundStartTimes || {}), [String(n)]: v } })),
        );
      })}

      {/* Left blank rather than guessed at. A final a week after the last
          round is a guess, and a wrong date on the calendar is worse than
          no date — nobody turns up to a blank. */}
      {stages.map(stage => row(
        stage.label, stage.key,
        (form.finalsStartDates || {})[stage.key] || '',
        (form.finalsStartTimes || {})[stage.key] || '',
        v => setForm(f => ({ ...f, finalsStartDates: { ...(f.finalsStartDates || {}), [stage.key]: v } })),
        v => setForm(f => ({ ...f, finalsStartTimes: { ...(f.finalsStartTimes || {}), [stage.key]: v } })),
      ))}
    </div>
  );
}

export default CompetitionSettingsFields;
